/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */
import React, { useState } from "react";
import { View, TextInput, TouchableOpacity, Platform } from "react-native";
import { TimePicker } from "baseui/timepicker";
import { TimezonePicker } from "baseui/timezonepicker";
import { Draggable } from "react-beautiful-dnd";
import Icon from "react-icons-kit";
import { warning } from "react-icons-kit/icomoon/warning";
import { closeRound } from "react-icons-kit/ionicons/closeRound";
import { arrows } from "react-icons-kit/entypo/arrows";

import { Text, styles } from "UpduoLibrary";
import {
  skaleModalErrorRed,
  upduoPrimaryBlack,
  skaleWebTextLightGray,
} from "config/UpduoColors";
import TimeWindowWarnModal from "./TimeWindowWarnModal";
import { TimeWindowType } from "graphql/internal/generated";
import {
  getTimeZoneOffsetDate,
  convertDateTypeToTimeString,
} from "config/Utilities";

export default function TimeWindowEditRow({
  timeWindowKey,
  summitInputStyles,
  dropdownItemHeight,
  timePickerOverrides,
  _handleShowUnsavedWarning,
  timeWindow,
  onChangeTimeWindow,
  onDelete,
}) {
  const [timeWindowTimeZone, setTimeWindowTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [utcTimeZoneOffset, setUtcTimeZoneOffset] = useState(
    new Date().getTimezoneOffset()
  );

  const timeWindowWarnIconVisible = false;
  const [timeWindowWarnModalVisible, setTimeWindowWarnModalVisible] =
    useState(false);

  const timeWindowStartDate = getTimeZoneOffsetDate(
    timeWindow.startTime,
    utcTimeZoneOffset
  );
  const timeWindowEndDate = getTimeZoneOffsetDate(
    timeWindow.endTime,
    utcTimeZoneOffset
  );

  const updateTimeWindowRow = (
    text,
    startDate,
    endDate,
    utcTimeZoneOffset,
    order
  ) => {
    const utcOffsetHours = Math.floor(utcTimeZoneOffset / 60);
    const utcOffsetMinutes = utcTimeZoneOffset % 60;
    for (const date of [startDate, endDate]) {
      date.setHours(date.getHours() + utcOffsetHours);
      date.setMinutes(date.getMinutes() + utcOffsetMinutes);
    }

    const newTimeWindow: TimeWindowType = {
      __typename: "TimeWindowType",
      id: timeWindow.id,
      startTime: convertDateTypeToTimeString(startDate),
      endTime: convertDateTypeToTimeString(endDate),
      text,
      order,
    };

    onChangeTimeWindow(newTimeWindow);
  };

  return (
    <Draggable draggableId={timeWindow.id} index={timeWindowKey} type={"step"}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <View style={{ flexDirection: "row" }}>
            <div
              style={{ marginTop: 50, marginRight: 15 }}
              {...provided.dragHandleProps}
            >
              <Icon
                style={{ color: skaleWebTextLightGray }}
                size={25}
                icon={arrows}
              />
            </div>
            <View
              style={{
                flexDirection: "column",
                width: 250,
                marginRight: 32,
              }}
            >
              <Text
                style={[
                  styles.webtitleText,
                  {
                    marginTop: 20,
                    marginBottom: 0,
                  },
                ]}
              >
                {"Short Description Text"}
              </Text>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  marginTop: 16,
                }}
              >
                <View
                  style={{
                    width: "100%",
                  }}
                >
                  <TextInput
                    style={[
                      styles.skillInput,
                      summitInputStyles,
                      { height: dropdownItemHeight },
                    ]}
                    value={timeWindow.text}
                    onChange={(event) => {
                      updateTimeWindowRow(
                        event.nativeEvent.text,
                        timeWindowStartDate,
                        timeWindowEndDate,
                        utcTimeZoneOffset,
                        timeWindow.order
                      );
                      _handleShowUnsavedWarning();
                    }}
                  />
                </View>
              </View>
            </View>

            <View
              style={{
                flexDirection: "column",
                width: 145,
                marginRight: 20,
              }}
            >
              <Text
                style={[
                  styles.webtitleText,
                  {
                    marginTop: 20,
                    marginBottom: 0,
                  },
                ]}
              >
                {"Window Start Time"}
              </Text>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  marginTop: 16,
                }}
              >
                <View
                  style={{
                    width: "100%",
                  }}
                >
                  <TimePicker
                    value={timeWindowStartDate}
                    onChange={(date) => {
                      updateTimeWindowRow(
                        timeWindow.text,
                        date,
                        timeWindowEndDate,
                        utcTimeZoneOffset,
                        timeWindow.order
                      );
                      _handleShowUnsavedWarning();
                    }}
                    step={1800}
                    creatable
                    overrides={timePickerOverrides}
                  />
                </View>
              </View>
            </View>

            <View>
              <Text
                style={[
                  styles.webtitleText,
                  {
                    marginTop: 20,
                    marginBottom: 0,
                    paddingLeft: 11,
                  },
                ]}
              >
                {" "}
              </Text>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                }}
              >
                <View
                  style={{
                    top: "50%",
                    width: "100%",
                    height: 2,
                    backgroundColor: upduoPrimaryBlack,
                  }}
                />
              </View>
            </View>

            <View
              style={{
                flexDirection: "column",
                width: 145,
                marginLeft: 20,
                marginRight: 32,
              }}
            >
              <Text
                style={[
                  styles.webtitleText,
                  {
                    marginTop: 20,
                    marginBottom: 0,
                  },
                ]}
              >
                {"Window End Time"}
              </Text>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  marginTop: 16,
                }}
              >
                <View
                  style={{
                    width: "100%",
                  }}
                >
                  <TimePicker
                    value={timeWindowEndDate}
                    onChange={(date) => {
                      updateTimeWindowRow(
                        timeWindow.text,
                        timeWindowStartDate,
                        date,
                        utcTimeZoneOffset,
                        timeWindow.order
                      );
                      _handleShowUnsavedWarning();
                    }}
                    step={1800}
                    creatable
                    overrides={timePickerOverrides}
                  />
                </View>
              </View>
            </View>

            <View
              style={{
                flexDirection: "column",
                width: 330,
              }}
            >
              <Text
                style={[
                  styles.webtitleText,
                  {
                    marginTop: 20,
                    marginBottom: 0,
                  },
                ]}
              >
                {"Timezone"}
              </Text>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  marginTop: 16,
                }}
              >
                <View
                  style={{
                    width: "100%",
                  }}
                >
                  <TimezonePicker
                    value={timeWindowTimeZone}
                    onChange={(timezone) => {
                      if (timezone?.offset) {
                        setTimeWindowTimeZone(timezone.id);
                        setUtcTimeZoneOffset(timezone.offset);
                        updateTimeWindowRow(
                          timeWindow.text,
                          timeWindowStartDate,
                          timeWindowEndDate,
                          timezone.offset,
                          timeWindow.order
                        );
                        _handleShowUnsavedWarning();
                      }
                    }}
                    overrides={timePickerOverrides}
                  />
                </View>
                <View style={{ top: "20%", marginLeft: 40 }}>
                  <Icon
                    style={{ color: skaleWebTextLightGray }}
                    size={16}
                    icon={closeRound}
                    onClick={() => {
                      onDelete(timeWindowKey);
                      _handleShowUnsavedWarning();
                    }}
                  />
                </View>
                {!timeWindowWarnIconVisible ? null : (
                  <TouchableOpacity
                    style={{
                      top: "20%",
                      marginLeft: 65,
                    }}
                    {...Platform.select({
                      web: {
                        onMouseEnter: () => {
                          setTimeWindowWarnModalVisible(true);
                        },
                        onMouseLeave: () => {
                          setTimeWindowWarnModalVisible(false);
                        },
                      },
                    })}
                  >
                    <Icon
                      size={18}
                      icon={warning}
                      style={{
                        color: skaleModalErrorRed,
                      }}
                    />

                    <TimeWindowWarnModal
                      isVisible={timeWindowWarnModalVisible}
                    />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        </div>
      )}
    </Draggable>
  );
}
